import { useState } from "react"
import styled from "styled-components"

import { Badge, Skeleton, spacing, Text } from "@minuthq/meatball-ui-react"

import { SearchFilter } from "src/components/Filter/SearchFilter"
import { Pager } from "src/components/Pager/Pager"
import { useFetchHomeGroup } from "src/data/homeGroups/queries/homeGroupQueries"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { useFetchHome } from "src/data/organizations/queries/homeQueries"
import { useFetchPropertyContexts } from "src/data/propertyContext/propertyContextQueries"
import { TPropertyContext } from "src/data/propertyContext/propertyContextTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { RadioGroup } from "src/ui/RadioGroup/RadioGroup"

export function AiAssistantSuggestionRadioSelect({
  value,
  onChange,
}: {
  value: string
  onChange: (context: TPropertyContext) => void
}) {
  const { tPlaceholder } = useTranslate()

  const { org } = useOrganization()

  const [searchInput, setSearchInput] = useState("")
  const [offset, setOffset] = useState(0)

  const limit = 4
  const fetchPropertyContexts = useFetchPropertyContexts({
    orgId: org.id,
    filters: {
      status: "RELEASED",
      title: searchInput,
      limit,
      offset,
    },
  })

  return (
    <div>
      <FilterWrapper>
        <SearchFilter
          initialValue={searchInput}
          onChange={(value) => setSearchInput(value)}
          placeholder={tPlaceholder("Search for content")}
        />
      </FilterWrapper>

      <RadioGroupWrapper>
        {!fetchPropertyContexts.isLoading ? (
          <RadioGroup
            value={value}
            options={
              fetchPropertyContexts.data?.property_contexts.map((context) => ({
                value: context.id,
                render: () => (
                  <AiAssistantSuggestionRadioSelectOption context={context} />
                ),
              })) ?? []
            }
            onChange={(value) => {
              const context =
                fetchPropertyContexts.data?.property_contexts.find(
                  (context) => context.id === value
                )
              if (context) {
                onChange(context)
              }
            }}
            hideRadio
          />
        ) : (
          <LoadingWrapper>
            <Skeleton variant="rect" height={48} />
            <Skeleton variant="rect" height={48} />
            <Skeleton variant="rect" height={48} />
          </LoadingWrapper>
        )}
      </RadioGroupWrapper>

      <Pager
        limit={limit}
        offset={offset}
        setOffset={setOffset}
        totalCount={fetchPropertyContexts.data?.paging.total_count ?? 0}
      />
    </div>
  )
}

function AiAssistantSuggestionRadioSelectOption({
  context,
}: {
  context: TPropertyContext
}) {
  const { tPlaceholder } = useTranslate()

  const fetchHome = useFetchHome({
    orgId: context.organization_id,
    homeId: context.home_id ?? "",
    options: {
      enabled: !!context.home_id,
    },
  })

  const fetchHomeGroup = useFetchHomeGroup({
    orgId: context.organization_id,
    homeGroupId: context.homegroup_id ?? "",
    options: {
      enabled: !!context.homegroup_id,
    },
  })

  function renderProperty() {
    if (!context.home_id && !context.homegroup_id) {
      return <Badge>{tPlaceholder("All units")}</Badge>
    }

    if (fetchHome.isInitialLoading || fetchHomeGroup.isInitialLoading) {
      return <Skeleton variant="circle" width="10ch" height={18} />
    }

    if (fetchHome.data) {
      return <Badge>{fetchHome.data.name}</Badge>
    }

    if (fetchHomeGroup.data) {
      return <Badge>{fetchHomeGroup.data.name}</Badge>
    }

    return <Badge color="warning">{tPlaceholder("Unknown")}</Badge>
  }

  return (
    <OptionWrapper>
      <Text>{context.title}</Text>

      {renderProperty()}
    </OptionWrapper>
  )
}

const FilterWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-bottom: ${spacing.XS};
`

const RadioGroupWrapper = styled.div`
  margin-bottom: ${spacing.XS};
`

const OptionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.M};
`

const LoadingWrapper = styled.div`
  display: grid;
  gap: ${spacing.XS};
`
